import "components/Map/Map.css";
import "leaflet/dist/leaflet.css";
import Map from "components/Map/Map";
import PageLayout from "layouts/PageLayout/PageLayout";
import { useParams } from "react-router-dom";
import { AdditionalCostFeeType, ProjectMap } from "generated/graphql";
import { useCustomGetProjectMapLazyQuery } from "generated/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useProjectMapMeasurements from "hooks/projectMaps/useProjectMapMeasurements";
import useUpdateProjectMap from "hooks/projectMaps/useUpdateProjectMap";
import MeasurementsSidebar from "modules/proposals/draft/measurements/sidebar/MeasurementsSidebar";
import CreateProposalFromMapDialog from "modules/projectMaps/CreateProposalFromMapDialog";
import {
  mapAddressToUpdateProjectMapInput,
  projectMapToMapAddress,
} from "modules/projectMaps/projectMapAddress";
import { normalizeStageMapData } from "modules/proposals/draft/measurements/stageMeasurements";
import { MapAddress } from "interfaces/interfaces";
import type { FeatureCollection } from "geojson";
import type { Map as LeafletMap } from "leaflet";
import {
  AI_MAPPING_JUST_CREATED_KEY,
  AiMappingSidebar,
  createAiMappingMapDriver,
  useAiMappingController,
} from "modules/aiMapping";
import { buildAiAssignmentSources } from "modules/aiMapping/aiAssignmentSources";
import { AiMappingAreaCreationControls } from "modules/aiMapping/components/AiMappingRunAreaEditor";
import AiMappingMeasuredAreaToolbar from "modules/aiMapping/components/AiMappingMeasuredAreaToolbar";
import useGetOrganization from "hooks/organization/useGetOrganization";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import useGetAdditionalCosts from "hooks/organization/useGetAdditionalCosts";
import {
  loadAiCostConfigs,
  removeAiCostConfig,
  replaceAiCostConfigs,
  upsertAiCostConfig,
} from "modules/aiMapping/aiMappingStorage";
import type {
  AiAssignmentPersistence,
  AiSavedAssignment,
} from "modules/proposals/draft/measurements/sidebar/AiProductAssignments";
import type { AssignmentSource } from "modules/aiMapping/aiAssignmentSources";

const isTouchDevice = (): boolean => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    navigator.msMaxTouchPoints > 0
  );
};

const useTouchControls = (): boolean => {
  const isMobile = isTouchDevice();
  return isMobile;
};

const isTouch = useTouchControls();

export default function MapPage() {
  const { mapId } = useParams<{ mapId: string }>();

  const [getProjectMap, { data: mapQueryData, loading }] = useCustomGetProjectMapLazyQuery();

  useEffect(() => {
    if (mapId) {
      getProjectMap({ variables: { id: mapId } });
    }
  }, [mapId, getProjectMap]);

  const projectMap = mapQueryData?.getProjectMap ?? null;

  const [updateProjectMap] = useUpdateProjectMap();

  const { data: organization } = useGetOrganization();

  const [justCreated, setJustCreated] = useState(false);
  useEffect(() => {
    try {
      const marker = sessionStorage.getItem(AI_MAPPING_JUST_CREATED_KEY);
      if (marker && marker === mapId) {
        setJustCreated(true);
        sessionStorage.removeItem(AI_MAPPING_JUST_CREATED_KEY);
      }
    } catch {
      /* ignore storage errors */
    }
  }, [mapId]);
  const showAiControls = !!organization?.canMapWithAi && !justCreated;
  const [aiSidebarCollapsed, setAiSidebarCollapsed] = useState(false);
  const [leafletMap, setLeafletMap] = useState<LeafletMap | null>(null);
  const { show: showGlobalMessage } = useGlobalMessage();
  const notify = useCallback(
    (message: string, variant: "success" | "error" | "warning" | "info") =>
      showGlobalMessage({ message, options: { variant } }),
    [showGlobalMessage]
  );
  const aiMappingDriver = useMemo(
    () => (leafletMap ? createAiMappingMapDriver(leafletMap) : null),
    [leafletMap]
  );
  useEffect(() => {
    return () => aiMappingDriver?.clearAll();
  }, [aiMappingDriver]);
  const aiMapping = useAiMappingController({
    projectMapId: mapId ?? null,
    driver: aiMappingDriver,
    enabled: showAiControls,
    notify,
    defaultAreaName: projectMap?.name ?? null,
    existingMeasurementIds: projectMap?.measurements?.map((m) => m.geojsonFeatureId) ?? [],
    onSplitLine: (line) => measuredAreaToolsRef.current?.splitLine(line) ?? 0,
    onErasePolygon: (points) => measuredAreaToolsRef.current?.erasePolygon(points) ?? 0,
    onMergePolygon: (points) => measuredAreaToolsRef.current?.mergePolygon(points) ?? 0,
  });

  const measurements = useProjectMapMeasurements({
    projectMap,
    aiMeasurementIds: aiMapping.aiMeasurementIds,
  });

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hadMeasurementsRef = useRef(false);
  if (measurements.measurements.length > 0) {
    hadMeasurementsRef.current = true;
  }
  useEffect(() => {
    if (!measurements.isDirty || !measurements.canSave || measurements.persisting) return;
    if (measurements.measurements.length === 0 && hadMeasurementsRef.current) {
      return;
    }
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      void measurements.save();
    }, 1500);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [measurements.isDirty, measurements.canSave, measurements.persisting, measurements.save]);

  const manualMeasurements = useMemo(
    () =>
      measurements.measurements
        .filter((measurement) => !aiMapping.aiMeasurementIds.includes(measurement.featureId))
        .map((measurement) => ({
          id: measurement.featureId,
          label: measurement.label,
          color: measurement.color ?? "#64748b",
          areaSqft: measurement.metrics.areaFeet,
          areaSqyd: measurement.metrics.areaYards,
          linearFeet: measurement.metrics.perimeterFeet,
          linearYards: measurement.metrics.perimeterYards,
        })),
    [measurements.measurements, aiMapping.aiMeasurementIds]
  );
  const measuredAreaToolsRef = useRef<{
    splitLine: (points: [number, number][]) => number;
    erasePolygon: (points: [number, number][]) => number;
    mergePolygon: (points: [number, number][]) => number;
    remove: () => void;
  } | null>(null);

  const manualAreaSqft = useMemo(
    () => manualMeasurements.reduce((total, measurement) => total + measurement.areaSqft, 0),
    [manualMeasurements]
  );
  const manualPerimeterFeet = useMemo(
    () => manualMeasurements.reduce((total, measurement) => total + measurement.linearFeet, 0),
    [manualMeasurements]
  );
  const aiSegmentedAreaSqft = useMemo(
    () =>
      aiMapping.allResults.reduce(
        (total, result) =>
          total +
          result.segmentations
            .filter((segmentation) => !segmentation.isDeleted)
            .reduce((areaTotal, segmentation) => areaTotal + (segmentation.areaSqft ?? 0), 0),
        0
      ),
    [aiMapping.allResults]
  );
  const combinedMeasuredAreaSqft = manualAreaSqft + aiSegmentedAreaSqft;
  const aiAssignmentSources = useMemo(
    () => buildAiAssignmentSources(aiMapping.allResults),
    [aiMapping.allResults]
  );
  const { data: additionalCostsData, loading: additionalCostsLoading } = useGetAdditionalCosts({
    page: 1,
    first: 100,
  });
  const additionalCostOptions = useMemo(
    () =>
      (additionalCostsData ?? [])
        .filter((cost) => !!cost?.id && !!cost?.name && cost.feeType === AdditionalCostFeeType.FLAT)
        .map((cost) => ({ id: cost.id, name: cost.name })),
    [additionalCostsData]
  );

  const [aiConfigsVersion, setAiConfigsVersion] = useState(0);
  const bumpAiConfigs = useCallback(() => setAiConfigsVersion((v) => v + 1), []);

  const sourceConfigCategory = (source: AssignmentSource): string =>
    source.kind === "ai_measured_area" ? source.id : source.id.replace(/^count:/, "");

  const aiPersistence: AiAssignmentPersistence = useMemo(() => {
    const mapId = projectMap?.id ?? "";
    return {
      onAssignSource: (source, productId, stallMode) => {
        const isMeasured = source.kind === "ai_measured_area";
        // A linear-feet value only makes sense on linear-foot products.
        const productJob = measurements.products?.find(
          (product) => product.id === productId
        )?.measurementJob;
        const effectiveMode =
          stallMode === "linear_ft" && productJob !== "LINEAR_FOOT" ? "count" : stallMode;
        if (effectiveMode !== stallMode) {
          showGlobalMessage({
            message: "Choose a linear foot product to price stalls by linear feet.",
            options: { variant: "warning" },
          });
        }
        const linearFeet =
          !isMeasured && effectiveMode === "linear_ft" && source.count
            ? Math.round((source.stallLinearFeet ?? 0) / source.count)
            : null;
        (source.areaIds ?? []).forEach((areaId) =>
          upsertAiCostConfig(mapId, {
            areaId,
            category: sourceConfigCategory(source),
            productId,
            kind: isMeasured ? "measured_area" : "detection_count",
            categories: source.categories ?? null,
            valueKind: linearFeet ? "linear_feet" : "count",
            linearFeetPerUnit: linearFeet,
          })
        );
        void aiMapping.syncAiSourceToAreas(
          source.areaIds ?? [],
          isMeasured
            ? { productId, kind: "measured_area", categories: source.categories }
            : {
                productId,
                kind: "detection_count",
                category: source.id.replace(/^count:/, ""),
                valueKind: effectiveMode === "linear_ft" ? "linear_feet" : "count",
                linearFeetPerUnit: linearFeet ?? undefined,
              }
        );
        bumpAiConfigs();
      },
      onUnassignSource: (source, productId) => {
        removeAiCostConfig(projectMap?.id ?? "", sourceConfigCategory(source), {
          additionalCostId: undefined,
          productId,
        });
        bumpAiConfigs();
      },
      onAssignCost: (source, additionalCostId, productId) => {
        if (!productId || !additionalCostId) return;
        const isMeasured = source.kind === "ai_measured_area";
        (source.areaIds ?? []).forEach((areaId) =>
          upsertAiCostConfig(mapId, {
            areaId,
            category: isMeasured ? source.id : source.id.replace(/^count:/, ""),
            productId,
            additionalCostId,
            kind: isMeasured ? "measured_area" : "detection_count",
            categories: source.categories ?? null,
          })
        );
        bumpAiConfigs();
      },
      onRemoveCost: (source, additionalCostId, productId) => {
        removeAiCostConfig(projectMap?.id ?? "", sourceConfigCategory(source), {
          additionalCostId,
          productId,
        });
        bumpAiConfigs();
      },
    };
  }, [projectMap?.id, aiMapping.syncAiSourceToAreas, bumpAiConfigs]);

  useEffect(() => {
    const mapId = projectMap?.id;
    if (!mapId) return;
    const configs = loadAiCostConfigs(mapId);
    if (!configs.length) return;
    const cleaned = configs.filter((config) => config.productId);
    if (cleaned.length !== configs.length) {
      replaceAiCostConfigs(mapId, cleaned);
      return;
    }
    if (!aiAssignmentSources.length) return;
    const sourceIds = new Set(aiAssignmentSources.map(sourceConfigCategory));
    const current = loadAiCostConfigs(mapId);
    const pruned = current.filter((config) => sourceIds.has(config.category));
    if (pruned.length !== current.length) replaceAiCostConfigs(mapId, pruned);
  }, [projectMap?.id, aiAssignmentSources]);

  useEffect(() => {
    const mapId = projectMap?.id;
    if (!mapId || additionalCostsLoading) return;
    const supportedCostIds = new Set(additionalCostOptions.map((cost) => cost.id));
    const configs = loadAiCostConfigs(mapId);
    const supportedConfigs = configs.filter(
      (config) => !config.additionalCostId || supportedCostIds.has(config.additionalCostId)
    );
    if (supportedConfigs.length !== configs.length) {
      replaceAiCostConfigs(mapId, supportedConfigs);
      bumpAiConfigs();
    }
  }, [projectMap?.id, additionalCostsLoading, additionalCostOptions, bumpAiConfigs]);

  const savedAiAssignments = useMemo<AiSavedAssignment[]>(() => {
    type SavedEntry = AiSavedAssignment;
    const entries: SavedEntry[] = [];
    loadAiCostConfigs(projectMap?.id ?? "").forEach((config) => {
      const sourceId =
        config.kind === "measured_area" ? config.category : `count:${config.category}`;
      let entry = entries.find(
        (candidate) => candidate.sourceId === sourceId && candidate.productId === config.productId
      );
      if (!entry) {
        entry = {
          sourceId,
          productId: null,
          stallMode: config.valueKind === "linear_feet" ? "linear_ft" : "count",
          additionalCostIds: [],
          jobSize: false,
        };
        entries.push(entry);
      }
      if (config.productId) entry.productId = config.productId;
      if (!config.additionalCostId) entry.jobSize = true;
      if (config.additionalCostId && !entry.additionalCostIds.includes(config.additionalCostId)) {
        entry.additionalCostIds.push(config.additionalCostId);
      }
    });
    return entries;
  }, [projectMap?.id, aiAssignmentSources, aiConfigsVersion]);

  const measuredAreaTools = (
    <AiMappingMeasuredAreaToolbar aiMapping={aiMapping} measurements={measurements} />
  );

  const initialAddress = useMemo(
    () => projectMapToMapAddress(projectMap),
    [
      projectMap?.id,
      projectMap?.addressLine1,
      projectMap?.addressLine2,
      projectMap?.addressCity,
      projectMap?.addressState,
      projectMap?.addressZip,
      projectMap?.addressCountry,
      projectMap?.latitude,
      projectMap?.longitude,
    ]
  );

  const handleAddressChange = useCallback(
    async (address: MapAddress | null) => {
      if (!projectMap?.id || !address?.line1) {
        return;
      }

      await updateProjectMap({
        variables: {
          input: mapAddressToUpdateProjectMapInput(projectMap.id, address),
        },
      });
    },
    [projectMap?.id, updateProjectMap]
  );

  const initialFeatures = useMemo(() => {
    if (!projectMap?.mapData) return null;
    try {
      const parsed = JSON.parse(projectMap.mapData) as FeatureCollection;
      return normalizeStageMapData(parsed);
    } catch {
      return null;
    }
  }, [projectMap?.mapData]);

  const initialCenter = useMemo((): [number, number] | null => {
    if (projectMap?.latitude != null && projectMap?.longitude != null) {
      return [projectMap.latitude, projectMap.longitude];
    }
    return null;
  }, [projectMap?.latitude, projectMap?.longitude]);

  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);

  const createIslandConfirmation = (): boolean => false;

  if (loading) {
    return (
      <PageLayout>
        <div style={{ padding: "24px", textAlign: "center" }}>Loading map...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div style={{ position: "relative", height: "100vh", overflow: "hidden" }}>
        <Map
          readonly={false}
          createIslandConfirmation={createIslandConfirmation}
          initialFeatures={initialFeatures}
          initialCenter={initialCenter}
          guidedControls={window.location.search.indexOf("guided") !== -1}
          isTouch={isTouch}
          goBack
          goBackTo="/map"
          goBackLabel="Back to Maps"
          onFeatureSelect={measurements.handleFeatureSelect}
          onChange={measurements.handleMapChange}
          highlightedFeatureId={measurements.selectedFeatureId}
          initialAddress={initialAddress}
          onAddressChange={handleAddressChange}
          onMapReady={setLeafletMap}
          suppressCreateHandling={
            aiMapping.drawSession != null ||
            aiMapping.splitModeActive ||
            aiMapping.eraseModeActive ||
            aiMapping.mergeModeActive ||
            !!aiMapping.splittingSegmentationId
          }
          controlsVariant={showAiControls ? "sidebar" : "card"}
          controlsCollapsed={aiSidebarCollapsed}
          onToggleControlsCollapse={() => setAiSidebarCollapsed((value) => !value)}
          mapTitle={projectMap?.name ?? null}
          hideMeasurementTotals={showAiControls}
          sidebarToolsContent={
            showAiControls ? <AiMappingAreaCreationControls controller={aiMapping} /> : null
          }
          measuredAreaTools={measuredAreaTools}
          measuredAreaToolsRef={measuredAreaToolsRef}
          controlsContent={
            showAiControls ? (
              <AiMappingSidebar
                controller={aiMapping}
                combinedMeasuredAreaSqft={combinedMeasuredAreaSqft}
                manualPerimeterFeet={manualPerimeterFeet}
                manualMeasurements={manualMeasurements}
              />
            ) : null
          }
        />
      </div>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1500,
          pointerEvents: "none",
        }}
      >
        <div style={{ pointerEvents: "auto", position: "absolute", top: 0, right: 0, bottom: 0 }}>
          <MeasurementsSidebar
            isOpen={measurements.isDrawerOpen}
            onToggle={measurements.toggleDrawer}
            measurements={measurements.measurements}
            selectedFeatureId={measurements.selectedFeatureId}
            onSelectFeature={measurements.selectFeature}
            products={measurements.products}
            groupedAssignments={measurements.groupedAssignments}
            aiMeasurementIds={aiMapping.aiMeasurementIds}
            aiAssignmentSources={aiAssignmentSources}
            additionalCosts={additionalCostOptions}
            savedAiAssignments={savedAiAssignments}
            aiPersistence={aiPersistence}
            onUpdateProduct={measurements.updateMeasurementProduct}
            onToggleScreenshot={measurements.toggleScreenshot}
            onSave={measurements.save}
            onApply={measurements.save}
            persisting={measurements.persisting}
            isDirty={measurements.isDirty}
            applyLoading={false}
            productsLoading={measurements.productsLoading}
            applyError={null}
            canSave={measurements.canSave}
            hideApply
            onCreateProposal={async () => {
              await measurements.save();
              setProposalDialogOpen(true);
            }}
          />
        </div>
      </div>
      {projectMap && (
        <CreateProposalFromMapDialog
          open={proposalDialogOpen}
          onClose={() => setProposalDialogOpen(false)}
          projectMap={projectMap as unknown as ProjectMap}
        />
      )}
    </PageLayout>
  );
}
